// SupportCall — "Talk to support" inside the app. The same BigAgri Support agent
// as the phone line and WhatsApp, in the browser, with two extra tools that only
// exist here: it can see which page the caller is on and which buttons are on it
// (screen_where_am_i), and point at one or open a page (screen_show_me). It
// points; it never clicks.
//
// Mount <SupportCallRoot/> once (App.tsx); open it with openSupportCall() (the
// ⌘K palette's "Talk to support…" and the headset on Home). The server half is
// support-voice-session: it checks this person's session, opens the agent
// session for THEM and THEIR active business, and hands back a one-time signed
// URL. Nothing the browser sends decides who is calling. What the agent may
// point at: the pages this person can reach and the tagged buttons
// (src/lib/supportGuide.ts), and the ordinary buttons on the current screen by
// label (src/lib/screenControls.ts, which never shares row or number labels).
//
// The panel floats: drag it by its top bar (or move it with the arrow keys on
// the grip) so it never covers what she is pointing at; it remembers the spot
// (src/lib/panelPosition.ts). It can shrink to a small bubble mid-call.
//
// The ElevenLabs SDK is loaded only when a call starts. Its audio worklets are
// served from /elevenlabs/ (see scripts/vendor-elevenlabs-worklets.mjs) so the
// CSP's script-src stays 'self'.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { GripHorizontal, Headset, Maximize2, Mic, MicOff, Minus, PhoneOff, Send, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import type { VoiceConversation, TextConversation } from '@elevenlabs/client'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { usePermissions } from '@/lib/usePermissions'
import { usePersistedState } from '@/hooks/usePersistedState'
import { navModulesFor } from '@/lib/modules'
import { pageIsDirty, wouldLeave } from '@/lib/pageDirty'
import { postEdgeFunction } from '@/lib/edgeFunctionPost'
import { asTicket, isTicketFiled, pageTitle, type HandoverTicket } from '@/lib/supportHandover'
import { SupportTicketCard } from '@/components/SupportTicketCard'
import { HelpNudge } from '@/components/HelpNudge'
import { NUDGE_EVENT, snoozeNudge } from '@/lib/helpNudge'
import { useSupportView } from '@/lib/platformView'
import { languageNote, spokenLanguage, type SpokenLanguage } from '@/lib/spokenLanguage'
import { BrandLoader } from '@/components/BrandLoader'
import {
  guideCatalogue, guideSelector, needsNavigation, openedPage, resolveGuide, screenSummary, type GuideTarget,
} from '@/lib/supportGuide'
import { findControl, isField, parseControlTarget, scanControls, scanErrors, tappedMessage } from '@/lib/screenControls'
import {
  clampPosition, DRAG_MIN_WIDTH, loadPosition, nudge, savePosition, type Point, type Size,
} from '@/lib/panelPosition'

const OPEN_EVENT = 'kf49:support-call-open'
const WORKLETS = {
  rawAudioProcessor:    '/elevenlabs/rawAudioProcessor.js',
  audioConcatProcessor: '/elevenlabs/audioConcatProcessor.js',
}
const HIGHLIGHT_MS = 20_000
// Firefox has no microphone sample-rate setting, so the SDK fetches a resampler
// from a CDN, which the CSP blocks. Say so instead of failing mid-start.
const UNSUPPORTED = typeof navigator !== 'undefined' && /firefox\//i.test(navigator.userAgent)
const FIND_MS = 5_000
const CONTROL_FIND_MS = 2_000
// The agent gives up on a client tool after its response_timeout (15 s), and a
// slow page opening hit that on 24 Oct: she then sat silent. Answer within
// SHOW_ME_MAX_MS instead, so she can carry on in words; a ring that turns up
// later still shows.
const SHOW_ME_MAX_MS = 6_000
const STILL_LOADING = 'Their screen is still loading, so nothing is ringed yet. Describe where it is in words; you can try again in a moment.'
const MAX_LINES = 30
// After the caller taps the ringed button, give the tap a moment to land (a page
// opens, a form appears) before she looks at the screen again.
const TAP_SETTLE_MS = 900
// After she opens a page, how long to wait for its buttons before answering
// (within SHOW_ME_MAX_MS): long enough for a page to render, short enough that
// the answer never feels slow. No buttons by then: the next "Screen now:" brings them.
const OPEN_SETTLE_MS = 2_500
// "Thinking" clears itself if no answer comes, so the B never spins forever.
const THINKING_MAX_MS = 20_000

// What the panel tells her when the call came from "Stuck? Talk to support".
const NUDGE_NOTE = 'The caller tapped "Stuck? Talk to support" after two saves on this page failed. Help with that save first: use the errors in "Screen now:" if it lists any.'

/** Open the panel. `nudged`: from the "Stuck?" bubble, so the call starts at once. */
export function openSupportCall(opts?: { nudged?: boolean }) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { nudged: opts?.nudged === true } }))
}

export function SupportCallRoot() {
  const [open, setOpen] = useState<{ nudged: boolean } | null>(null)
  const [nudge, setNudge] = useState(false)
  // Not in support view: the server refuses a call there (as HelpBar).
  const inSupportView = useSupportView()
  useEffect(() => {
    const onOpen = (e: Event) => { setNudge(false); setOpen({ nudged: (e as CustomEvent<{ nudged?: boolean }>).detail?.nudged === true }) }
    const onNudge = () => setNudge(true)
    window.addEventListener(OPEN_EVENT, onOpen)
    window.addEventListener(NUDGE_EVENT, onNudge)
    return () => { window.removeEventListener(OPEN_EVENT, onOpen); window.removeEventListener(NUDGE_EVENT, onNudge) }
  }, [])
  if (open) return <SupportCallPanel nudged={open.nudged} onClose={() => { setOpen(null); setNudge(false) }} />
  if (!nudge || inSupportView || UNSUPPORTED) return null
  return <HelpNudge onTalk={() => openSupportCall({ nudged: true })} onDismiss={() => { snoozeNudge(); setNudge(false) }} />
}

// ---------------------------------------------------------------------------
// Finding what to point at.
// ---------------------------------------------------------------------------
const isVisible = (el: Element) => el.getClientRects().length > 0
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))

/** Poll `find` until it answers or `ms` pass: a page that was just opened, or a
 *  menu that is opening, renders a beat later. */
async function waitFor<T>(find: () => T | null, ms: number): Promise<T | null> {
  const until = Date.now() + ms
  for (;;) {
    const hit = find()
    if (hit || Date.now() >= until) return hit
    await sleep(150)
  }
}

const findTagged = (id: string) =>
  waitFor(() => Array.from(document.querySelectorAll(guideSelector(id))).find(isVisible) ?? null, FIND_MS)

const screenControlLabels = () => scanControls(document, isVisible).map(c => c.label)
const screenErrors = () => scanErrors(document, isVisible)

// ---------------------------------------------------------------------------
// screen_show_me, split by kind of target. Each returns the sentence the agent
// reads back; none of them clicks anything.
// ---------------------------------------------------------------------------
type Ring = (el: Element, label: string) => void
const RINGED = (label: string) => `Ringed "${label}" on their screen in green. Tell them to tap it.`

async function showControl(wanted: string, ring: Ring): Promise<string> {
  const hit = await waitFor(() => findControl(scanControls(document, isVisible), wanted), CONTROL_FIND_MS)
  if (!hit) return `No single button called "${wanted.slice(0, 40)}" is on their screen now. Call screen_where_am_i for the current list, or describe where it is in words.`
  ring(hit.el, hit.label)
  return RINGED(hit.label)
}

async function showCatalogue(
  target: unknown, at: { pathname: string; search: string; catalogue: GuideTarget[] },
  navigate: (to: string) => void, ring: Ring,
  screen: { now: () => string; path: () => string },
): Promise<string> {
  const found = resolveGuide(target, at.catalogue)
  if (!found.ok) return found.say
  const t = found.target
  if (needsNavigation(t, at.pathname)) {
    // Never throw away someone's work to show them a button.
    if (wouldLeave(t.path!, at.pathname + at.search) && pageIsDirty()) {
      return 'The caller has unsaved work on this page. Ask them to save or finish it first, then try again.'
    }
    navigate(t.path!)
  }
  if (!t.highlight) {
    // Wait until the app is ON the opened page and its buttons are there (the
    // old page has buttons too), then hand them over in this answer.
    const settled = await waitFor(
      () => (!needsNavigation(t, screen.path()) && screenControlLabels().length ? true : null), OPEN_SETTLE_MS)
    return openedPage(t.label, settled ? screen.now() : null)
  }
  const el = await findTagged(t.id)
  if (!el) return `Opened the page, but "${t.label}" is not on their screen (they may not have the right to use it, or the page is still loading). Describe where it is in words.`
  ring(el, t.label)
  return RINGED(t.label)
}

// ---------------------------------------------------------------------------
// The highlight: a glowing ring with a "Tap here" tag that follows one element
// until it is tapped, Escape is pressed, or 20 seconds pass. pointer-events:
// none, so the tap reaches the button underneath. Taps inside the support panel
// (mute, typing) leave it up.
// ---------------------------------------------------------------------------
interface Highlight { el: Element; label: string }

function useTrackedRect(el: Element): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null)
  useEffect(() => {
    let frame = 0
    const track = () => { setRect(el.getBoundingClientRect()); frame = requestAnimationFrame(track) }
    track()
    return () => cancelAnimationFrame(frame)
  }, [el])
  return rect
}

function GuideRing({ highlight, onDone, onTapped }: { highlight: Highlight; onDone: () => void; onTapped: (label: string) => void }) {
  const rect = useTrackedRect(highlight.el)
  useEffect(() => {
    const timer = setTimeout(onDone, HIGHLIGHT_MS)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDone() }
    const inPanel = (t: Node | null) => !!(t as Element | null)?.closest?.('[data-support-panel]')
    const onRinged = (t: Node | null) => !!t && highlight.el.contains(t)
    // A press anywhere else ends the highlight at once.
    const onPress = (e: PointerEvent) => {
      const target = e.target as Node | null
      if (!inPanel(target) && !onRinged(target)) onDone()
    }
    // Only a real CLICK on the ringed button is the step done: a finger that
    // starts a scroll on it presses but never clicks (review, 27 Oct).
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node | null
      if (inPanel(target) || !onRinged(target)) return
      // A field is different: tapping a box to type in is the start of the step.
      if (!isField(highlight.el)) onTapped(highlight.label)
      onDone()
    }
    // Capture, so the tap that presses the button also ends the highlight.
    window.addEventListener('pointerdown', onPress, true)
    window.addEventListener('click', onClick, true)
    window.addEventListener('keydown', onKey)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('pointerdown', onPress, true)
      window.removeEventListener('click', onClick, true)
      window.removeEventListener('keydown', onKey)
    }
  }, [highlight, onDone, onTapped])
  if (!rect) return null
  const pad = 6
  const below = rect.top < 80
  const ring: CSSProperties = { left: rect.left - pad, top: rect.top - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
  const tag: CSSProperties = { left: Math.max(8, Math.min(rect.left - pad, window.innerWidth - 272)), top: below ? rect.bottom + pad + 10 : rect.top - pad - 44 }
  return (
    <>
      <div aria-hidden="true" className="pointer-events-none fixed z-[70] rounded-xl border-[3px] border-emerald-400 shadow-[0_0_0_6px_rgba(52,211,153,0.25),0_0_28px_rgba(52,211,153,0.55)] animate-pulse"
        style={ring} />
      <div aria-hidden="true" className="pointer-events-none fixed z-[70] max-w-[16rem] rounded-full bg-emerald-400 light:bg-emerald-800 px-3 py-1.5 text-sm font-semibold text-black shadow-lg flex items-center gap-1.5"
        style={tag}>
        <span aria-hidden="true">{below ? '↑' : '↓'}</span> Tap here: {highlight.label}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// The catalogue for THIS person: the same reachable-pages list as the palette.
// ---------------------------------------------------------------------------
function useGuideCatalogue(): GuideTarget[] {
  const { pathname } = useLocation()
  const { data: me } = useCurrentUser()
  const { data: tenant } = useActiveTenant()
  const { perms } = usePermissions()
  const [appMode] = usePersistedState<'packhouse' | 'farm'>('kf49.appMode', 'packhouse')
  const navMode = pathname.startsWith('/farm/') ? 'farm' : appMode
  return useMemo(() => guideCatalogue(
    navModulesFor(me ?? null, { kind: tenant?.kind, enabledModules: tenant?.enabledModules, appMode: navMode, permMap: perms, slug: tenant?.slug })
      .map(m => ({ id: m.id, key: m.key, label: m.label, path: m.path })),
  ), [me, perms, tenant?.kind, tenant?.enabledModules, tenant?.slug, navMode])
}

// ---------------------------------------------------------------------------
// Placement: drag by the top bar, arrow keys on the grip, remembered spot,
// always clamped inside the window. Docked along the bottom on phones.
// ---------------------------------------------------------------------------
const viewSize = (): Size => ({ w: window.innerWidth, h: window.innerHeight })

function usePanelPlacement(ref: RefObject<HTMLElement>) {
  const [pos, setPos] = useState<Point | null>(() => loadPosition())
  const [wide, setWide] = useState(() => window.innerWidth >= DRAG_MIN_WIDTH)
  const grab = useRef<{ dx: number; dy: number } | null>(null)
  const sizeOf = useCallback((): Size => {
    const r = ref.current?.getBoundingClientRect()
    return { w: r?.width ?? 380, h: r?.height ?? 320 }
  }, [ref])
  const place = useCallback((p: Point) => setPos(clampPosition(p, sizeOf(), viewSize())), [sizeOf])

  // Keep it inside the window when the window, or the panel itself, resizes.
  useEffect(() => {
    const refit = () => { setWide(window.innerWidth >= DRAG_MIN_WIDTH); setPos(p => (p ? clampPosition(p, sizeOf(), viewSize()) : p)) }
    refit()
    window.addEventListener('resize', refit)
    const ro = typeof ResizeObserver !== 'undefined' && ref.current ? new ResizeObserver(refit) : null
    if (ro && ref.current) ro.observe(ref.current)
    return () => { window.removeEventListener('resize', refit); ro?.disconnect() }
  }, [ref, sizeOf])

  const handle = {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      if (!wide || (e.target as Element).closest('button:not([data-grip]), input, a')) return
      const r = ref.current!.getBoundingClientRect()
      grab.current = { dx: e.clientX - r.left, dy: e.clientY - r.top }
      try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* pointer already gone */ }
    },
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
      if (grab.current) place({ x: e.clientX - grab.current.dx, y: e.clientY - grab.current.dy })
    },
    onPointerUp: () => {
      if (!grab.current) return
      grab.current = null
      setPos(p => { savePosition(p); return p })
    },
    onDoubleClick: () => { savePosition(null); setPos(null) },
  }
  const onGripKey = (e: React.KeyboardEvent) => {
    const from = pos ?? (() => { const r = ref.current!.getBoundingClientRect(); return { x: r.left, y: r.top } })()
    const next = nudge(from, e.key)
    if (!next) return
    e.preventDefault()
    const p = clampPosition(next, sizeOf(), viewSize())
    setPos(p); savePosition(p)
  }
  const style: CSSProperties | undefined = wide && pos ? { left: pos.x, top: pos.y } : undefined
  return { style, wide, handle, onGripKey }
}

// ---------------------------------------------------------------------------
// The voice circle: grows with whoever is talking. Updated by a frame loop on a
// ref, not by React state, so it costs no re-renders.
// ---------------------------------------------------------------------------
type Tone = 'agent' | 'you' | 'idle'

const TONE_CLASS: Record<Tone, string> = {
  agent: 'from-emerald-300 to-emerald-600 shadow-[0_0_32px_rgba(52,211,153,0.55)]',
  you:   'from-sky-300 to-sky-600 shadow-[0_0_32px_rgba(56,189,248,0.5)]',
  idle:  'from-emerald-200/60 to-emerald-700/60',
}

function VoiceOrb({ level, tone, size }: { level: () => number; tone: Tone; size: number }) {
  const dot = useRef<HTMLDivElement>(null)
  useEffect(() => {
    let frame = 0
    let smooth = 0
    const tick = () => {
      smooth = smooth * 0.7 + Math.min(1, Math.max(0, level())) * 0.3
      if (dot.current) dot.current.style.transform = `scale(${(1 + smooth * 0.45).toFixed(3)})`
      frame = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(frame)
  }, [level])
  return (
    <div aria-hidden="true" className="relative grid place-items-center" style={{ width: size, height: size }}>
      {tone !== 'idle' && <span className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" />}
      <div ref={dot} className={`rounded-full bg-gradient-to-br transition-colors duration-300 ${TONE_CLASS[tone]}`}
        style={{ width: size * 0.72, height: size * 0.72 }} />
    </div>
  )
}

/** The voice circle, or the BigAgri B tracing itself while she works out an
 *  answer (after you stop talking, after you send, during a lookup). */
function CallIndicator({ thinking, level, tone, size }: { thinking: boolean; level: () => number; tone: Tone; size: number }) {
  if (!thinking) return <VoiceOrb level={level} tone={tone} size={size} />
  return (
    <div className="grid place-items-center" style={{ width: size, height: size }}>
      <BrandLoader label={null} className={size >= 64 ? 'w-14 h-14' : 'w-7 h-7'} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pieces of the panel.
// ---------------------------------------------------------------------------
type CallStatus = 'idle' | 'starting' | 'live' | 'ended' | 'error'
interface Line { who: 'you' | 'agent'; text: string; note?: boolean }

function statusText(status: CallStatus, speaking: boolean, muted: boolean, thinking = false): string {
  if (status === 'starting') return 'Connecting…'
  if (status !== 'live') return status === 'ended' ? 'Call ended' : 'Ready when you are'
  if (speaking) return 'Support is talking…'
  if (thinking) return 'Thinking…'
  return muted ? 'Muted: she can’t hear you' : 'Listening…'
}

function Transcript({ lines }: { lines: Line[] }) {
  const end = useRef<HTMLLIElement>(null)
  useEffect(() => { end.current?.scrollIntoView?.({ block: 'end' }) }, [lines])
  if (lines.length === 0) return null
  return (
    <ol aria-label="Conversation" className="max-h-56 overflow-y-auto flex flex-col gap-2 text-sm pr-1">
      {lines.map((l, i) => l.note ? (
        <li key={i} className="self-center text-xs text-fg-muted">{l.text}</li>
      ) : (
        <li key={i} className={l.who === 'you' ? 'self-end max-w-[85%]' : 'self-start max-w-[85%]'}>
          <p className={l.who === 'you'
            ? 'rounded-2xl rounded-br-md bg-sky-600 text-white px-3 py-2'
            : 'rounded-2xl rounded-bl-md surface border hairline text-fg px-3 py-2'}>
            {l.text}
          </p>
        </li>
      ))}
      <li ref={end} aria-hidden="true" />
    </ol>
  )
}

const ROUND_BTN = 'min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-full transition-colors'

function CallControls({ draft, setDraft, onSend, muted, onMute, onEnd }: {
  draft: string; setDraft: (v: string) => void; onSend: () => void
  muted: boolean; onMute: () => void; onEnd: () => void
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 flex items-center rounded-full surface border hairline-strong pl-4 pr-1 focus-within:border-emerald-500">
        <input value={draft} onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') onSend() }}
          placeholder="Type instead…" aria-label="Type a message to support"
          className="flex-1 min-w-0 min-h-[44px] bg-transparent text-sm text-fg placeholder-fg-muted outline-none" />
        <button type="button" onClick={onSend} aria-label="Send" disabled={!draft.trim()}
          className={`${ROUND_BTN} text-emerald-400 disabled:opacity-30`}>
          <Send size={16} />
        </button>
      </div>
      <button type="button" onClick={onMute} aria-label={muted ? 'Unmute' : 'Mute'} aria-pressed={muted}
        className={`${ROUND_BTN} border hairline-strong ${muted ? 'bg-amber-500 text-black' : 'surface text-fg'}`}>
        {muted ? <MicOff size={18} /> : <Mic size={18} />}
      </button>
      <button type="button" onClick={onEnd} aria-label="End the call"
        className={`${ROUND_BTN} bg-red-600 hover:bg-red-500 text-white`}>
        <PhoneOff size={18} />
      </button>
    </div>
  )
}

type Rated = 'idle' | 'sending' | 'thanks' | 'failed'
interface CallToRate { sessionKey: string; conversationId: string | null }

/** The call to rate: our session key from the start answer; the vendor id comes later. */
const callToRate = (vars: Record<string, string | number | boolean>): CallToRate =>
  ({ sessionKey: String(vars.session_key ?? ''), conversationId: null })

/** The vendor's conversation id, if the SDK has one. A nicety: the session key is enough. */
function vendorId(c: { getId: () => string }): string | null {
  try { return c.getId() } catch { return null }
}

/** "Did that help?" after a call that was a conversation. One tap, then thanks. */
function RateCall({ rated, onRate }: { rated: Rated; onRate: (up: boolean) => void }) {
  if (rated === 'thanks') return <p className="text-sm text-fg-muted text-center" aria-live="polite">Thanks, that helps us improve.</p>
  return (
    <div className="flex items-center justify-center gap-2" aria-live="polite">
      <p className="text-sm text-fg">Did that help?</p>
      <button type="button" onClick={() => onRate(true)} disabled={rated === 'sending'} aria-label="Yes, that helped"
        className={`${ROUND_BTN} surface border hairline-strong text-emerald-400 disabled:opacity-40`}>
        <ThumbsUp size={18} />
      </button>
      <button type="button" onClick={() => onRate(false)} disabled={rated === 'sending'} aria-label="No, that did not help"
        className={`${ROUND_BTN} surface border hairline-strong text-red-400 disabled:opacity-40`}>
        <ThumbsDown size={18} />
      </button>
      {rated === 'failed' && <p className="text-xs text-fg-muted">Could not save. Try again.</p>}
    </div>
  )
}

function StartArea({ status, onStart }: { status: CallStatus; onStart: () => void }) {
  return (
    <div className="flex flex-col gap-3 text-center">
      <p className="text-sm text-fg-muted">
        Ask out loud, or type. She sees the page, its buttons and fields, and any
        error showing, never what you type. She rings what to tap, one step at a
        time. The call is recorded.
      </p>
      {UNSUPPORTED ? (
        <p className="text-sm text-amber-400">
          Talk to support does not work in Firefox yet. Open BigAgri in Chrome, Edge or Safari, or use Report a problem.
        </p>
      ) : (
        <button type="button" onClick={onStart}
          className="btn-primary tap-target-y inline-flex items-center justify-center gap-2 rounded-full">
          <Mic size={16} aria-hidden="true" /> {status === 'idle' ? 'Start talking' : 'Talk again'}
        </button>
      )}
    </div>
  )
}

interface SessionStart { ok: true; signed_url: string; dynamic_variables: Record<string, string | number | boolean> }

// ---------------------------------------------------------------------------
// The panel.
// ---------------------------------------------------------------------------
// fallow-ignore-next-line complexity
function SupportCallPanel({ onClose, nudged = false }: { onClose: () => void; nudged?: boolean }) {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const catalogue = useGuideCatalogue()
  const [status, setStatus] = useState<CallStatus>('idle')
  const [speaking, setSpeaking] = useState(false)
  const [muted, setMuted] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lines, setLines] = useState<Line[]>([])
  const [draft, setDraft] = useState('')
  const [highlight, setHighlight] = useState<Highlight | null>(null)
  const [thinking, setThinking] = useState(false)
  const conv = useRef<VoiceConversation | TextConversation | null>(null)
  const panel = useRef<HTMLElement>(null)
  const { style, wide, handle, onGripKey } = usePanelPlacement(panel)
  // Bumped by every start and every end. A start that finds it has moved on
  // (X tapped while connecting, or the panel unmounted) ends what it opened:
  // otherwise startSession resolves into a live, recorded call with no panel.
  const attempt = useRef(0)
  // The call to rate when it ends: our session key, and the vendor's id once known.
  const call = useRef<CallToRate | null>(null)
  const [rated, setRated] = useState<Rated>('idle')
  // The ticket card once she files one, and the last button she rang (for the ticket).
  const [ticket, setTicket] = useState<HandoverTicket | null>(null)
  const lastRung = useRef('')
  // The caller's language as last told to her, for TYPED messages, and only
  // until she has heard the caller speak: the speech recogniser reads these
  // notes too and then turns Afrikaans speech into English (sandbox, 27 Oct:
  // 4 runs in 5). Typed-only switching with the note: 21/21; without: 17/20.
  const lastLang = useRef<SpokenLanguage | null>(null)
  const spoke = useRef(false)
  const lastTyped = useRef('')
  const noteLanguage = useCallback((text: string) => {
    const lang = spokenLanguage(text)
    if (spoke.current || !lang || lang === lastLang.current || !conv.current) return
    lastLang.current = lang
    conv.current.sendContextualUpdate(languageNote(lang))
  }, [])

  // The tools read the LATEST page and catalogue, not the ones at call start.
  const live = useRef({ pathname, search, catalogue, speaking })
  live.current = { pathname, search, catalogue, speaking }

  const clearHighlight = useCallback(() => setHighlight(null), [])
  const ring = useCallback((el: Element, label: string) => {
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    lastRung.current = label
    setHighlight({ el, label })
  }, [])

  // The screen she was last told about, so a page change and a tap do not send the
  // same "Screen now:" twice.
  const lastScreen = useRef('')
  const sendScreen = useCallback(() => {
    const c = conv.current
    if (!c) return
    const now = `Screen now: ${screenSummary({ pathname: live.current.pathname, catalogue: live.current.catalogue, controls: screenControlLabels(), errors: screenErrors() })}`
    if (now === lastScreen.current) return
    lastScreen.current = now
    c.sendContextualUpdate(now)
  }, [])

  // "She sees you tap": the caller tapped the button she rang. Let it land, show
  // her the new screen, then tell her, so she goes straight to the next step.
  const onRingTapped = useCallback((label: string) => {
    setLines(prev => [...prev, { who: 'you' as const, text: `You tapped ${label}`, note: true }].slice(-MAX_LINES))
    setThinking(true)
    setTimeout(() => {
      if (!conv.current) return
      sendScreen()
      conv.current.sendUserMessage(tappedMessage(label))
    }, TAP_SETTLE_MS)
  }, [sendScreen])

  const clientTools = useMemo(() => ({
    screen_where_am_i: () => { setThinking(true); return screenSummary({
      pathname: live.current.pathname, catalogue: live.current.catalogue, controls: screenControlLabels(), errors: screenErrors(),
    }) },
    screen_show_me: async (params: { target?: unknown }) => {
      // An ordinary button on the current screen, by its label; else the catalogue.
      setThinking(true)
      const wanted = parseControlTarget(params?.target)
      const screen = {
        now: () => screenSummary({
          pathname: live.current.pathname, catalogue: live.current.catalogue, controls: screenControlLabels(), errors: screenErrors(),
        }),
        path: () => live.current.pathname,
      }
      const shown = wanted ? showControl(wanted, ring) : showCatalogue(params?.target, live.current, navigate, ring, screen)
      return Promise.race([shown, sleep(SHOW_ME_MAX_MS).then(() => STILL_LOADING)])
    },
  }), [navigate, ring])

  const end = useCallback(async () => {
    attempt.current++
    const c = conv.current
    conv.current = null
    setHighlight(null)
    if (c) await c.endSession().catch(() => {})
  }, [])

  // The B never spins forever: an answer that does not come clears it.
  useEffect(() => {
    if (!thinking) return
    const t = setTimeout(() => setThinking(false), THINKING_MAX_MS)
    return () => clearTimeout(t)
  }, [thinking])

  // Closing the panel or leaving the app ends the call; it must never keep the
  // microphone open behind the caller's back.
  useEffect(() => () => { void end() }, [end])

  // Hand her the screen BEFORE she asks: on connect and after every page change,
  // once the page has had a moment to render. Saves a screen_where_am_i round
  // trip (about 1.5 s) on most answers. Page NAME and button labels only, never
  // the path (it can carry a record id; the vendor keeps it 30 days).
  useEffect(() => {
    if (status !== 'live') return
    const timer = setTimeout(sendScreen, 800)
    return () => clearTimeout(timer)
  }, [pathname, status, catalogue, sendScreen])

  // "She sees errors": an error that appears mid-call (a save that fails, a field
  // turning red) reaches her at once, without a page change. sendScreen dedupes,
  // so unrelated page updates cost nothing.
  useEffect(() => {
    if (status !== 'live' || typeof MutationObserver === 'undefined') return
    let timer: ReturnType<typeof setTimeout> | undefined
    const observer = new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(sendScreen, 700) })
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['role', 'aria-invalid'] })
    return () => { observer.disconnect(); clearTimeout(timer) }
  }, [status, sendScreen])

  // Who is talking, for the voice circle: her output while she speaks, else the mic.
  const level = useCallback(() => {
    const c = conv.current as (VoiceConversation & Partial<Record<'getInputVolume' | 'getOutputVolume', () => number>>) | null
    if (!c) return 0
    try { return live.current.speaking ? c.getOutputVolume?.() ?? 0 : c.getInputVolume?.() ?? 0 } catch { return 0 }
  }, [])

  // She filed a ticket: send the screen for it and show the card (support-handover
  // checks the call and the ticket are the caller's own). No card on a failure:
  // she still says the reference out loud.
  const handover = useCallback(async () => {
    const c = call.current
    if (!c?.sessionKey) return
    try {
      const r = await postEdgeFunction<HandoverTicket & { ok?: boolean }>('support-handover', {
        session_key: c.sessionKey, conversation_id: c.conversationId,
        path: live.current.pathname, title: pageTitle(document.title), rung: lastRung.current,
      }, { timeoutMs: 10_000, timeoutMessage: 'Could not load your ticket.', failMessage: 'Could not load your ticket.', retryAction: 'ask again' })
      const t = asTicket(r)
      if (t) setTicket(t)
    } catch { /* no card */ }
  }, [])

  const start = async () => {
    const mine = ++attempt.current
    const stale = () => attempt.current !== mine
    setError(null); setLines([]); setStatus('starting'); setMuted(false)
    try {
      const s = await postEdgeFunction<SessionStart>('support-voice-session', {}, {
        timeoutMs: 15_000,
        timeoutMessage: 'Support did not answer in time. Check your connection and try again.',
        failMessage: 'Could not start a support call.',
        retryAction: 'start the call again',
      })
      if (stale()) return
      call.current = callToRate(s.dynamic_variables)
      setRated('idle'); setTicket(null); lastRung.current = ''; lastLang.current = null; spoke.current = false; lastTyped.current = ''
      const { Conversation } = await import('@/lib/elevenlabsClient')
      if (stale()) return
      const opened = await Conversation.startSession({
        signedUrl: s.signed_url,
        connectionType: 'websocket',
        dynamicVariables: s.dynamic_variables,
        clientTools,
        workletPaths: WORKLETS,
        onConnect: () => setStatus('live'),
        onDisconnect: () => { conv.current = null; setStatus('ended'); setHighlight(null); setSpeaking(false); setThinking(false) },
        onModeChange: ({ mode }) => { setSpeaking(mode === 'speaking'); if (mode === 'speaking') setThinking(false) },
        onMessage: ({ message, role }) => {
          // Your words arrive once you stop talking: she is now working out the answer.
          setThinking(role === 'user')
          // A caller line that is not the text they just typed was spoken.
          if (role === 'user' && message.trim() !== lastTyped.current) spoke.current = true
          setLines(prev => [...prev, { who: role === 'user' ? 'you' as const : 'agent' as const, text: message }].slice(-MAX_LINES))
        },
        onError: (message) => setError(message || 'The call dropped.'),
        onAgentToolResponse: (p) => { if (isTicketFiled(p)) void handover() },
      })
      if (stale()) { await opened.endSession().catch(() => {}); return }
      conv.current = opened
      call.current.conversationId = vendorId(opened)
      if (nudged) {
        opened.sendContextualUpdate(NUDGE_NOTE)
        setLines(prev => [...prev, { who: 'you' as const, text: 'Your last two saves here did not go through. Tell her what you were trying to do.', note: true }])
      }
    } catch (e) {
      if (stale()) return
      const msg = e instanceof Error ? e.message : String(e)
      setError(/permission|NotAllowed/i.test(msg)
        ? 'The microphone is blocked. Allow it for this site in the browser settings, then try again.'
        : msg)
      setStatus('error')
    }
  }

  // From the "Stuck?" bubble: the tap that opened the panel starts the call.
  // (Not guarded by a ref: a remount ends the first try, see the unmount effect.)
  const startRef = useRef(start)
  startRef.current = start
  useEffect(() => { if (nudged) void startRef.current() }, [nudged])

  const toggleMute = () => {
    const c = conv.current as VoiceConversation | null
    if (!c || !('setMicMuted' in c)) return
    c.setMicMuted(!muted); setMuted(!muted)
  }

  const sendTyped = () => {
    const text = draft.trim()
    if (!text || !conv.current) return
    noteLanguage(text) // typed: the note reaches her before the message
    lastTyped.current = text
    conv.current.sendUserMessage(text)
    setThinking(true)
    setLines(prev => [...prev, { who: 'you' as const, text }].slice(-MAX_LINES))
    setDraft('')
  }

  // Thumbs at the end: stored on OUR session row (support-call-rating checks it is
  // the caller's own), and from there in the Monday report.
  const rate = async (up: boolean) => {
    const c = call.current
    if (!c?.sessionKey) return
    setRated('sending')
    try {
      await postEdgeFunction('support-call-rating', { session_key: c.sessionKey, conversation_id: c.conversationId, rating: up ? 'up' : 'down' }, {
        timeoutMs: 10_000, timeoutMessage: 'Could not save your rating.', failMessage: 'Could not save your rating.', retryAction: 'rate the call again',
      })
      setRated('thanks')
    } catch {
      setRated('failed')
    }
  }

  const close = () => { void end(); onClose() }
  const inCall = status === 'starting' || status === 'live'
  const tone: Tone = status !== 'live' ? 'idle' : speaking ? 'agent' : muted ? 'idle' : 'you'
  const isThinking = status === 'live' && thinking && !speaking
  const said = statusText(status, speaking, muted, isThinking)
  const place = wide && style ? '' : 'bottom-4 right-4 left-4 sm:left-auto'

  if (collapsed) {
    return (
      <>
        {highlight && <GuideRing highlight={highlight} onDone={clearHighlight} onTapped={onRingTapped} />}
        <aside ref={panel} data-support-panel aria-label="Talk to support (small)" style={style} {...handle}
          className={`fixed z-[60] ${place} flex items-center gap-2 rounded-full surface-sunk border hairline-strong shadow-2xl pl-2 pr-1 py-1 ${wide ? 'cursor-grab active:cursor-grabbing' : ''}`}>
          <CallIndicator thinking={isThinking} level={level} tone={tone} size={40} />
          <p className="text-sm text-fg pr-1" aria-live="polite">{said}</p>
          <button type="button" onClick={() => setCollapsed(false)} aria-label="Open the support panel"
            className={`${ROUND_BTN} text-fg-muted hover:text-fg`}>
            <Maximize2 size={16} />
          </button>
          {inCall && (
            <button type="button" onClick={() => { void end() }} aria-label="End the call"
              className={`${ROUND_BTN} bg-red-600 hover:bg-red-500 text-white`}>
              <PhoneOff size={16} />
            </button>
          )}
        </aside>
      </>
    )
  }

  return (
    <>
      {highlight && <GuideRing highlight={highlight} onDone={clearHighlight} onTapped={onRingTapped} />}
      {/* Not a dialog on purpose: the caller must be able to tap the page behind
          it while support points at things, so there is no backdrop or focus trap. */}
      <aside ref={panel} data-support-panel aria-label="Talk to support" style={style}
        className={`fixed z-[60] ${place} sm:w-[380px] surface-sunk border hairline-strong rounded-3xl shadow-2xl overflow-hidden flex flex-col`}>
        <header {...handle} title={wide ? 'Drag to move · double-click to put it back' : undefined}
          className={`flex items-center gap-1 px-2 py-1 border-b hairline bg-gradient-to-r from-emerald-500/15 to-transparent select-none ${wide ? 'cursor-grab active:cursor-grabbing' : ''}`}>
          {wide && (
            <button type="button" data-grip onKeyDown={onGripKey} aria-label="Move the support panel (arrow keys)"
              className={`${ROUND_BTN} text-fg-muted cursor-grab`}>
              <GripHorizontal size={18} />
            </button>
          )}
          <Headset size={18} className="text-emerald-400 ml-1" aria-hidden="true" />
          <h2 className="flex-1 text-sm font-semibold text-fg pl-1">BigAgri support</h2>
          <button type="button" onClick={() => setCollapsed(true)} aria-label="Make the support panel small"
            className={`${ROUND_BTN} text-fg-muted hover:text-fg`}>
            <Minus size={18} />
          </button>
          <button type="button" onClick={close} aria-label="Close and end the call"
            className={`${ROUND_BTN} text-fg-muted hover:text-fg`}>
            <X size={18} />
          </button>
        </header>

        <div className="p-4 flex flex-col gap-3">
          <div className="flex flex-col items-center gap-1">
            <CallIndicator thinking={isThinking} level={level} tone={tone} size={88} />
            <p className="text-sm font-medium text-fg" aria-live="polite">{said}</p>
            {/* Consent (POPIA, review 27 Oct): the notice shows while a call
                connects, and for the whole of a call the Stuck? bubble started. */}
            {(status === 'starting' || (nudged && inCall)) && (
              <p className="text-sm font-semibold text-fg">This call is recorded.</p>
            )}
          </div>

          {highlight && (
            <p className="self-center rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium px-3 py-1">
              Showing you: {highlight.label}
            </p>
          )}

          {ticket && <SupportTicketCard ticket={ticket} />}
          {status === 'ended' && lines.length > 0 && call.current?.sessionKey && <RateCall rated={rated} onRate={up => { void rate(up) }} />}
          {!inCall && <StartArea status={status} onStart={start} />}
          {error && <p role="alert" className="text-sm text-red-400 text-center">{error}</p>}
          <Transcript lines={lines} />
          {inCall && (
            <CallControls draft={draft} setDraft={setDraft} onSend={sendTyped}
              muted={muted} onMute={toggleMute} onEnd={() => { void end() }} />
          )}
        </div>
      </aside>
    </>
  )
}
